// THE ENGAGEMENT'S OWN VOCABULARY — CONTENT-TRANSLATION-ARCH §3.3.
//
// The owner's requirement was that translations "work with the original vocabulary, and that this
// vocabulary is used consistently throughout an engagement". This panel is where that vocabulary
// is decided. It is also, deliberately, the shape of P1: the seeding agent fills the PROPOSED
// column and can do nothing else — a proposed term changes no translation, and does not even move
// the terminology hash — and every term that reaches the translator got there because a person
// activated it here.
//
// The health line at the top is the layer reporting on itself: how much of this engagement exists
// in all three languages, how much a human has corrected, and how much is waiting for a human
// because the source moved after they corrected it. The operator should never have to guess.
import { useCallback, useEffect, useState } from 'react';

import { Zone, ZoneHead, Sheet } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { translationsApi, type GlossaryPayload, type TranslationStatusPayload } from '../api/translations.js';
import { LANGUAGE_LABEL } from '../../shared/language.js';
import type { GlossaryTerm } from '../../shared/contentTranslation.js';
import './TerminologyPanel.de.js';
import './TerminologyPanel.fr.js';

interface Draft {
  term_de: string;
  term_en: string;
  term_fr: string;
  invariant: boolean;
  note: string;
}

const emptyDraft = (): Draft => ({ term_de: '', term_en: '', term_fr: '', invariant: false, note: '' });

export function TerminologyPanel({ engagementId }: { engagementId: number }) {
  const t = useT();
  const { tp } = useLocale();
  const [data, setData] = useState<GlossaryPayload | null>(null);
  const [status, setStatus] = useState<TranslationStatusPayload | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, s] = await Promise.all([
        translationsApi.glossary(engagementId),
        translationsApi.status(engagementId).catch(() => null),
      ]);
      setData(g);
      setStatus(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [engagementId]);

  useEffect(() => { void load(); }, [load]);

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); await load(); } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }, [load]);

  const add = () => run(async () => {
    await translationsApi.createTerm(engagementId, {
      term_de: draft.term_de.trim(),
      term_en: draft.term_en.trim(),
      term_fr: draft.term_fr.trim(),
      invariant: draft.invariant,
      note: draft.note.trim() || null,
    });
    setDraft(emptyDraft());
  });

  const seed = () => {
    setSeeding(true); setError(null);
    void (async () => {
      try { await translationsApi.seed(engagementId); await load(); } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally { setSeeding(false); }
    })();
  };

  const startEdit = (term: GlossaryTerm) => {
    setEditingId(term.id);
    setEditDraft({
      term_de: term.term_de, term_en: term.term_en, term_fr: term.term_fr,
      invariant: term.invariant, note: term.note ?? '',
    });
  };

  const saveEdit = (term: GlossaryTerm) => run(async () => {
    await translationsApi.patchTerm(engagementId, term.id, {
      term_de: editDraft.term_de.trim(),
      term_en: editDraft.term_en.trim(),
      term_fr: editDraft.term_fr.trim(),
      invariant: editDraft.invariant,
      note: editDraft.note.trim() || null,
    });
    setEditingId(null);
  });

  const proposed = (data?.terms ?? []).filter((x) => x.status === 'proposed');
  const active = (data?.terms ?? []).filter((x) => x.status === 'active');
  const retired = (data?.terms ?? []).filter((x) => x.status === 'retired');

  const row = (term: GlossaryTerm) => {
    const editing = editingId === term.id;
    const d = editing ? editDraft : { ...term, note: term.note ?? '' } as unknown as Draft;
    return (
      <div className="tm-row" key={term.id}>
        {editing ? (
          <>
            <input className="form-input" value={editDraft.term_de} aria-label={LANGUAGE_LABEL.de}
                   onChange={(e) => setEditDraft({ ...editDraft, term_de: e.target.value })} />
            <input className="form-input" value={editDraft.term_en} disabled={editDraft.invariant} aria-label={LANGUAGE_LABEL.en}
                   onChange={(e) => setEditDraft({ ...editDraft, term_en: e.target.value })} />
            <input className="form-input" value={editDraft.term_fr} disabled={editDraft.invariant} aria-label={LANGUAGE_LABEL.fr}
                   onChange={(e) => setEditDraft({ ...editDraft, term_fr: e.target.value })} />
            <div className="tm-verbs">
              <button type="button" className="btn btn-accent btn-sm" disabled={busy || !editDraft.term_de.trim()}
                      onClick={() => void saveEdit(term)}>{t('Save')}</button>
              <button type="button" className="lnk" onClick={() => setEditingId(null)}>{t('Cancel')}</button>
            </div>
            <label className="tm-note">
              <input type="checkbox" checked={editDraft.invariant}
                     onChange={(e) => setEditDraft({ ...editDraft, invariant: e.target.checked })} />
              {' '}{t('Never translate this term — it reads the same in every language')}
            </label>
          </>
        ) : (
          <>
            <div className={`tm-term${term.invariant ? ' tm-term--inv' : ''}`}>{term.term_de}</div>
            <div className={`tm-term${term.invariant ? ' tm-term--inv' : ''}`}>{term.term_en}</div>
            <div className={`tm-term${term.invariant ? ' tm-term--inv' : ''}`}>{term.term_fr}</div>
            <div className="tm-verbs">
              <span className="tm-src" title={term.source === 'seed_agent'
                ? t('Proposed by the terminology pass over this engagement’s own documents')
                : t('Written by an operator')}>
                {term.source === 'seed_agent' ? t('seeded term') : t('operator term')}
              </span>
              {term.status === 'proposed' ? (
                <>
                  <button type="button" className="btn btn-accent btn-sm" disabled={busy}
                          onClick={() => void run(() => translationsApi.patchTerm(engagementId, term.id, { status: 'active' }))}>
                    {t('Activate')}
                  </button>
                  <button type="button" className="lnk" onClick={() => startEdit(term)}>{t('Edit')}</button>
                  <button type="button" className="lnk" disabled={busy}
                          onClick={() => void run(() => translationsApi.discardTerm(engagementId, term.id))}>
                    {t('Discard')}
                  </button>
                </>
              ) : term.status === 'active' ? (
                <>
                  <button type="button" className="lnk" onClick={() => startEdit(term)}>{t('Edit')}</button>
                  <button type="button" className="lnk" disabled={busy}
                          onClick={() => void run(() => translationsApi.patchTerm(engagementId, term.id, { status: 'retired' }))}>
                    {t('Retire')}
                  </button>
                </>
              ) : (
                <button type="button" className="lnk" disabled={busy}
                        onClick={() => void run(() => translationsApi.patchTerm(engagementId, term.id, { status: 'active' }))}>
                  {t('Put back in use')}
                </button>
              )}
            </div>
            {d.note ? <div className="tm-note">{d.note}</div> : null}
          </>
        )}
      </div>
    );
  };

  return (
    <Zone id="z-terminology">
      <ZoneHead
        label={t('Terminology')}
        count={active.length}
        note={t('The words this engagement uses. Every translation carries them; changing one retranslates what the machine wrote, and leaves what you wrote alone.')}
        route={
          <button type="button" className="lnk" disabled={seeding} onClick={seed}>
            {seeding ? t('Reading the documents…') : t('Propose terms from this engagement')}
          </button>
        }
      />
      <Sheet pad>
        {status ? (
          <div className="tm-note" style={{ marginTop: 0, marginBottom: 12 }}>
            {t('{done} of {expected} translations in place', { done: status.translated, expected: status.expected })}
            {status.pending ? ` · ${t('{n} still to come', { n: status.pending })}` : ''}
            {status.operator ? ` · ${t('{n} corrected by hand', { n: status.operator })}` : ''}
            {status.stale_operator ? ` · ${t('{n} need your review — the source changed after you corrected them', { n: status.stale_operator })}` : ''}
            {status.flagged ? ` · ${t('{n} miss a term', { n: status.flagged })}` : ''}
          </div>
        ) : null}

        {error ? <div className="tx-tools-err" style={{ marginBottom: 10 }}>{error}</div> : null}

        {proposed.length ? (
          <>
            <div className="section-label mb-8">{t('Proposed — nothing here affects a translation until you activate it')}</div>
            {proposed.map(row)}
          </>
        ) : null}

        <div className="section-label mb-8 mt-16">{t('In use')}</div>
        {active.length ? active.map(row) : <div className="rw-none">{t('No terms yet — every translation is made without a shared vocabulary.')}</div>}

        <div className="tm-form">
          <input className="form-input" placeholder={LANGUAGE_LABEL.de} value={draft.term_de}
                 onChange={(e) => setDraft({ ...draft, term_de: e.target.value })} />
          <input className="form-input" placeholder={LANGUAGE_LABEL.en} value={draft.term_en} disabled={draft.invariant}
                 onChange={(e) => setDraft({ ...draft, term_en: e.target.value })} />
          <input className="form-input" placeholder={LANGUAGE_LABEL.fr} value={draft.term_fr} disabled={draft.invariant}
                 onChange={(e) => setDraft({ ...draft, term_fr: e.target.value })} />
          <button type="button" className="btn btn-accent btn-sm" disabled={busy || !draft.term_de.trim()} onClick={() => void add()}>
            {t('Add the term')}
          </button>
          <label className="tm-note">
            <input type="checkbox" checked={draft.invariant}
                   onChange={(e) => setDraft({ ...draft, invariant: e.target.checked })} />
            {' '}{t('Never translate this term — it reads the same in every language')}
          </label>
        </div>

        {retired.length ? (
          <>
            <button type="button" className="lnk" onClick={() => setShowRetired(!showRetired)}>
              {showRetired ? t('Hide retired terms') : tp(retired.length, '{n} retired term', '{n} retired terms', { n: retired.length })}
            </button>
            {showRetired ? retired.map(row) : null}
          </>
        ) : null}
      </Sheet>
    </Zone>
  );
}
